import { Box, Container, Flex, Grid, Heading, SystemStyleObject, Text } from '@chakra-ui/react';
import { NumberStatsSection } from '@components/NumberStatsSection';
import { StarBackground } from '@components/StarBackground';
import { PROJECTS_BY_ID } from '@content/partners';
import {
  AccordionItem,
  AccordionItemContent,
  AccordionItemTrigger,
  AccordionRoot,
  APP_ROUTES_ABSOLUTE,
  AppLink,
  AppLinkWithArrow,
  Badge,
  BadgeColor,
  Card,
  EthereumIconWhite,
  GradientShadowOverlay,
  Image,
  LANDING_ROUTES,
  LinksWithCuttedBorders,
  PolygonIconWhite,
  pxToRem,
  RectangleDecoration,
  VideoAutoplay,
} from '@cyberkongz/ui';
import { keyframes } from '@emotion/react';
import { PageSEOTags } from '@features/seo/SEO';
import { useAPIEcosystemSummary } from '@hooks/useDataSummary';
import { PARTICLES_CONFIG, useParticles } from '@hooks/useParticles';
import { useInView } from 'react-intersection-observer';
import Particles from 'react-tsparticles';

import { APIEcosystemSummarySuccessResponse } from './api/ecosystem-summary';

function WelcomeSection() {
  const { particlesInit } = useParticles();

  return (
    <Flex
      css={{
        pt: [pxToRem(135), pxToRem(150)],
        pb: [pxToRem(80), pxToRem(150)],
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'url(/static/banana/intro_bg.jpg) bottom center/cover no-repeat',
        position: 'relative',
      }}
    >
      <Container position="relative" zIndex="2">
        <Box css={{ textAlign: 'center' }}>
          <Heading mb={[0, -4]}>everything</Heading>
          <Heading
            variant="displayPrimary"
            css={{
              fontSize: ['9xl', pxToRem(250)],
              textShadow: '0px 0px 30px #0D8050',
            }}
          >
            $banana
          </Heading>
          <Box
            css={{
              position: 'relative',
              display: 'inline-flex',
              mt: [pxToRem(-80), pxToRem(-160)],
            }}
          >
            <VideoAutoplay
              src="/static/banana/intro.webm"
              posterExtension="png"
              css={{ maxWidth: [pxToRem(237), pxToRem(410)], position: 'relative', zIndex: 2 }}
            />
            <Image
              src="/static/banana/intro_pod.png"
              alt="Banana green pod"
              css={{ position: 'absolute', bottom: 0, left: 0, transform: 'translateY(22.5%)' }}
            />
            <Box
              css={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: pxToRem(400),
                height: pxToRem(400),
                '#tsparticles': {
                  width: 'inherit',
                  height: 'inherit',
                },
              }}
            >
              <Particles id="tsparticles" init={particlesInit} options={PARTICLES_CONFIG} />
            </Box>
          </Box>
        </Box>
      </Container>
    </Flex>
  );
}

function BananaCoreSection() {
  return (
    <Box css={{ width: '100%', position: 'relative' }}>
      <GradientShadowOverlay top css={{ top: -5 }} />
      <Container position="relative" zIndex="2">
        <Heading css={{ maxWidth: '20em', lineHeight: 'base' }}>
          Banana sits at the core of everything in the{' '}
        </Heading>
        <Heading variant="displaySecondary" mt={[2, 3]}>
          cyberkongz universe
        </Heading>
      </Container>
      <Container variant="md">
        <Flex css={{ mt: [8, 16], flexDirection: ['column', 'row'] }}>
          <Box css={{ position: 'relative' }}>
            <VideoAutoplay
              src="/static/banana/golden_banana.webm"
              fallbackVideoExtension="mp4"
              posterExtension="jpg"
            />
            <Box
              css={{
                position: 'absolute',
                top: '-2%',
                left: '-10%',
                width: '120%',
                height: '110%',
                bg: 'radial-gradient(51.86% 51.86% at 50% 44.02%, rgba(0, 0, 0, 0) 0%, #000 100%)',
              }}
            />
          </Box>
          <Box css={{ pt: 10, zIndex: 1, pl: [0, 10, 20] }}>
            <Box mb={8}>
              <RectangleDecoration
                sxRectangle={{ w: pxToRem(74) }}
                stylesWrapper={{ w: pxToRem(180), mb: 8 }}
              />
            </Box>
            <Box css={{ color: 'textSecondary', maxWidth: '31.125em', lineHeight: '1.75' }}>
              As the CyberKongz universe grows, more utility will be added. The mission of
              CyberKongz extends further than a 2D PFP or a 3D avatar. CyberKongz is driven by
              community, utility, and of course the main life source of any ape, $BANANA! $BANANA is
              the utility token that fuels the CyberKongz ecosystem. It is NOT an investment and has
              NO economic value.
            </Box>
          </Box>
        </Flex>
      </Container>
    </Box>
  );
}

const UTILITY_ITEMS = [
  {
    title: 'Yield $BANANA',
    description:
      'Each Genesis Kong produces 10 $BANANA every day until March 18th 2031. The total $BANANA produced for entire collection over the 10 year contract is 36,500,000 $BANANA.',
    image: 'banana.png',
  },
  {
    title: 'BREED',
    description:
      'Hold any two Genesis Kongz in your wallet and burn 600 $BANANA to receive an incubator.',
    image: 'incubator.gif',
    badgeVariant: 'primary',
    badgeLabel: 'Completed',
  },
  {
    title: 'CUSTOMIZE',
    description:
      'Got lore for your Kongz? 100 $BANANA and it is on the blockchain. Custom name needed? That`s 10 $BANANA. VX name needs an update? That`ll only be 2 $BANANA.',
    image: 'customize.png',
  },
  {
    title: 'PLAY & KOLLECT',
    description:
      'With 0.4 $BANANA , you can send one of your CyberKongz VX on a jungle adventure to kollect exclusive NFTs, Kongium, parts of the seasonal $BANANA prize pool and more.',
    image: 'kongium.png',
  },
  {
    title: 'VOTE',
    description: (
      <Box>
        $BANANA isn`t only a Utility Token, it`s part of our{' '}
        <AppLink
          href={LANDING_ROUTES.BLOG_POST(
            'cyberkongz-ooh-a-first-iteration-of-community-governance'
          )}
        >
          voting system
        </AppLink>{' '}
        !OOH
      </Box>
    ),
    image: 'vote.png',
  },
];

const floatAnimation = keyframes`
  0% {
    transform: translate(0) rotateZ(0deg);
  }
  25% {
    transform: translate(0px, 8px) rotateZ(1.5deg);
  }
  75% {
    transform: translate(0px, -8px) rotateZ(-1.5deg);
  }
  100% {
    transform: translate(0) rotateZ(0deg);
  }
`;

function UtilitiesAnimationImage({
  inView,
  image,
  alt,
  css,
}: {
  inView: boolean;
  image: string;
  alt: string;
  css?: SystemStyleObject;
}) {
  return (
    <Image
      src={`/static/banana/${image}.png`}
      css={{
        position: 'absolute',
        top: ['-10%', '3%'],
        right: pxToRem(40),
        transform: inView
          ? 'rotateZ(0deg)'
          : [
              `translate(${pxToRem(-200)}, 100%) rotateZ(180deg)`,
              `translate(${pxToRem(-500)}, 100%) rotateZ(180deg)`,
            ],
        opacity: inView ? 1 : 0.3,
        transition: '1.25s ease-in-out',
        animation: inView ? `${floatAnimation} 6s 1.25s ease-in-out infinite` : 'none',
        ...css,
      }}
      alt={alt}
    />
  );
}

function UtilitiesAnimation({ inView }: { inView: boolean }) {
  return (
    <Box>
      <UtilitiesAnimationImage
        {...{
          inView,
          image: 'bunch_1',
          alt: 'Banana bunch',
          css: {
            maxWidth: [pxToRem(60), '100%'],
          },
        }}
      />
      <UtilitiesAnimationImage
        {...{
          inView,
          image: 'bunch_2',
          alt: 'Banana bunch',
          css: {
            top: ['-1%', '20%'],
            right: pxToRem(80),
            maxWidth: [pxToRem(155), '100%'],
          },
        }}
      />
      <UtilitiesAnimationImage
        {...{
          inView,
          image: 'crate_1',
          alt: 'Crate',
          css: {
            top: ['-2%', '18%'],
            right: ['3%', '-7%'],
            maxWidth: [pxToRem(75), '100%'],
          },
        }}
      />
      <UtilitiesAnimationImage
        {...{
          inView,
          image: 'crate_2',
          alt: 'Crate',
          css: {
            top: ['20%', '60%'],
            right: '10%',
            maxWidth: [pxToRem(105), '100%'],
          },
        }}
      />
      <UtilitiesAnimationImage
        {...{
          inView,
          image: 'bunch_2_rotated',
          alt: 'Banana bunch',
          css: {
            top: ['9%', '44%'],
            right: [pxToRem(-50), pxToRem(-190)],
            maxWidth: [pxToRem(155), '100%'],
            filter: 'blur(8px)',
          },
        }}
      />
    </Box>
  );
}

function UtilitiesSection() {
  const { ref, inView } = useInView({
    threshold: 0.6,
    triggerOnce: true,
  });

  return (
    <Box mt={[20, 32]} mb={[16, 20]} pb={8}>
      <Container ref={ref} position="relative" zIndex="2">
        <Box position="relative" zIndex="2">
          <Heading size="sm" mb={8}>
            Utilities
          </Heading>
          <Grid gap={[4, 6]} mt={[4, 8]} maxW={pxToRem(712)}>
            {UTILITY_ITEMS.map(({ title, description, image, badgeVariant, badgeLabel }) => (
              <Card variant="landingTransparent" size="xl" key={title}>
                {badgeVariant && badgeLabel ? (
                  <Badge css={{ mb: 4 }} badgeColor={badgeVariant as BadgeColor} withDot>
                    {badgeLabel}
                  </Badge>
                ) : null}
                <Flex css={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <Box>
                    <Heading size="2xs" letterSpacing="0.03em" mb={2} fontSize={['sm', 'lg']}>
                      {title}
                    </Heading>
                    <Box
                      letterSpacing="0.01em"
                      maxWidth="34.375em"
                      css={{
                        '& a': { textDecoration: 'underline' },
                      }}
                    >
                      {description}
                    </Box>
                  </Box>
                  <Image
                    w={[pxToRem(30), pxToRem(50)]}
                    src={`/static/icons/${image}`}
                    alt={`${title} image`}
                  />
                </Flex>
              </Card>
            ))}
          </Grid>
        </Box>
        <UtilitiesAnimation {...{ inView }} />
      </Container>
    </Box>
  );
}

const GAME_UTILITY_ITEMS = [
  {
    title: 'sandbox',
    description: 'Part of our experience will utilize banana as main token/card to access them.',
    image: PROJECTS_BY_ID.sandbox.image,
    alt: PROJECTS_BY_ID.sandbox.label,
  },
  {
    title: 'solarbots',
    description:
      '$BANANA burn mechanism purposely built into the Solarbots economy through upgrading and crafting of CyberKongz Solarbots combat units.',
    image: PROJECTS_BY_ID.solarbots.image,
    alt: PROJECTS_BY_ID.solarbots.label,
  },
  {
    title: 'webb',
    description: '$BANANA will be part of the CyberKongz experiences.',
    image: PROJECTS_BY_ID.webb.image,
    alt: PROJECTS_BY_ID.webb.label,
  },
  {
    title: 'the littles',
    description: 'Use $BANANA to earn NFTs from their Arcade Experiences',
    image: PROJECTS_BY_ID.the_littles.image,
    alt: PROJECTS_BY_ID.the_littles.label,
  },
  {
    title: 'angry dynos',
    description: 'Burn tokens to build up masterpieces, which requires a lot of resources.',
    image: PROJECTS_BY_ID.angry_dynomites_lab.image,
    alt: PROJECTS_BY_ID.angry_dynomites_lab.label,
  },
];

function GameUtilitiesSection() {
  return (
    <Box>
      <Container>
        <Heading size="sm" mb={8}>
          Game Utilities
        </Heading>
        <AccordionRoot collapsible>
          <Grid gap={[4, 6]} mt={[4, 8]} maxW={pxToRem(712)}>
            {GAME_UTILITY_ITEMS.map(({ title, description, image, alt }) => (
              <AccordionItem key={title} value={title}>
                <Card variant="landing" size="xl">
                  <Flex
                    css={{
                      display: ['none', 'flex'],
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box>
                      <Heading size="2xs" mb={[0, 2]}>
                        {title}
                      </Heading>
                      <Text css={{ letterSpacing: '0.01em', maxWidth: '28.125em' }}>
                        {description}
                      </Text>
                    </Box>
                    <Image css={{ maxWidth: pxToRem(140) }} src={image} alt={alt} />
                  </Flex>
                  <Box display={['block', 'none']}>
                    <AccordionItemTrigger>
                      <Heading size="2xs" mb={[0, 2]}>
                        {title}
                      </Heading>
                    </AccordionItemTrigger>
                    <AccordionItemContent>
                      <Image css={{ maxWidth: pxToRem(140) }} src={image} alt={alt} />
                      <Text css={{ letterSpacing: '0.01em', maxWidth: '28.125em' }}>
                        {description}
                      </Text>
                    </AccordionItemContent>
                  </Box>
                </Card>
              </AccordionItem>
            ))}
          </Grid>
        </AccordionRoot>
      </Container>
    </Box>
  );
}

function NUMBER_STATS(data?: APIEcosystemSummarySuccessResponse['banana']) {
  return [
    { number: data?.totalSupply.toLocaleString(), label: 'total supply' },
    { number: data?.circCupply.toLocaleString(), label: 'circulating supply' },
    { number: data?.burnt.toLocaleString(), label: 'burnt' },
  ];
}

const AVAILABLE_ON_ITEMS = [
  {
    icon: <EthereumIconWhite />,
    label: 'Ethereum',
    links: [{ href: LANDING_ROUTES.BANANA_ETH, label: 'ETH Contract' }],
  },
  {
    icon: <PolygonIconWhite />,
    label: 'Polygon',
    links: [{ href: LANDING_ROUTES.BANANA_POLY, label: 'Polygon Contract' }],
  },
];

function AvailableOnSection() {
  return (
    <Box
      css={{
        pb: [20, pxToRem(180)],
      }}
    >
      <Container>
        <Heading textAlign={['left', 'center']}>Available on</Heading>
        <Grid css={{ mt: 16, gap: 8, maxWidth: pxToRem(602), mx: 'auto' }}>
          {AVAILABLE_ON_ITEMS.map(({ icon, label, links }) => (
            <Card variant="landingTransparent" key={label}>
              <Flex
                css={{
                  alignItems: ['flex-start', 'center'],
                  justifyContent: 'space-between',
                  gap: 3,
                  flexDirection: ['column', 'row'],
                }}
              >
                <Flex css={{ alignItems: 'center', gap: 3 }}>
                  {icon}
                  <Box
                    css={{
                      fontSize: 'lg',
                      fontWeight: 'medium',
                      textTransform: 'uppercase',
                    }}
                  >
                    {label}
                  </Box>
                </Flex>
                <Flex css={{ flexDirection: ['column', 'row'], gap: [3, 4], fontSize: 'lg' }}>
                  {links.map(({ href, label }) => (
                    <AppLinkWithArrow key={label} href={href} sxIcon={{ width: 4, height: 4 }}>
                      {label}
                    </AppLinkWithArrow>
                  ))}
                </Flex>
              </Flex>
            </Card>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

const LINKS = [
  {
    title: 'meet all the kongz in',
    emphasis: 'the kollections!',
    href: LANDING_ROUTES.KOLLECTIONS,
  },
  {
    title: 'join the adventure',
    emphasis: 'explore the jungle',
    href: APP_ROUTES_ABSOLUTE.JUNGLE_ADVENTURE,
  },
  { title: 'Check out our', emphasis: 'blueprint', href: LANDING_ROUTES.BLUEPRINT },
];

function LinksSection() {
  return (
    <Box
      css={{
        pt: [20, 32],
        pb: [20, 48],
        backgroundImage: 'url(/static/banana/links_bg.jpg)',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: ['center top -50px', 'center'],
        position: 'relative',
      }}
    >
      <GradientShadowOverlay top height={[pxToRem(159), pxToRem(380)]} />
      <Container zIndex={2} position="relative">
        <LinksWithCuttedBorders links={LINKS} />
      </Container>
      <GradientShadowOverlay height={[pxToRem(216), pxToRem(210)]} />
    </Box>
  );
}

export default function BananaPage() {
  const { data } = useAPIEcosystemSummary();

  return (
    <>
      <PageSEOTags pageKey="banana" />
      <Box css={{ width: '100%' }}>
        <WelcomeSection />
        <Box
          css={{
            position: 'relative',
            '&:before': {
              position: 'absolute',
              display: 'block',
              content: '""',
              left: ['-118%', '-68%'],
              top: ['34.78%', '29.78%'],
              width: ['194%', '131%'],
              height: ['119%', '164%'],
              maxW: pxToRem(1892),
              maxHeight: [pxToRem(1000), pxToRem(1440)],
              background:
                'radial-gradient(43.39% 43.39% at 50% 50%, rgba(69, 240, 140, 0.24) 0%, rgba(69, 240, 140, 0) 100%)',
            },
          }}
        >
          <StarBackground>
            <BananaCoreSection />
            <UtilitiesSection />
            <GameUtilitiesSection />
          </StarBackground>
        </Box>
        <Box
          css={{
            background: 'url(/static/banana/available_bg.png) bottom center/cover no-repeat',
          }}
        >
          <NumberStatsSection numberStats={NUMBER_STATS(data?.banana)} />
          <AvailableOnSection />
        </Box>
        <LinksSection />
      </Box>
    </>
  );
}
